import React, { useState, useEffect, useCallback } from 'react';
import { userAPI } from '../../api';

const UserFundTransfer = () => {
  const [children, setChildren] = useState([]);
  const [depositWallet, setDepositWallet] = useState(0);
  const [loadingChildren, setLoadingChildren] = useState(true);

  const [selected, setSelected] = useState(null); // selected child object
  const [searchCode, setSearchCode] = useState('');
  const [amount, setAmount] = useState('');
  const [password, setPassword] = useState('');

  const [step, setStep] = useState(1); // 1 = select + amount, 2 = confirm
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null); // { type, text }

  const parsedAmount = Number(amount);
  const isAmountValid =
    amount !== '' &&
    !Number.isNaN(parsedAmount) &&
    Number.isFinite(parsedAmount) &&
    parsedAmount > 0 &&
    /^\d+(\.\d{1,2})?$/.test(String(amount)) &&
    parsedAmount >= 280;

  const fetchChildren = useCallback(async () => {
    setLoadingChildren(true);
    try {
      const res = await userAPI.getMyDirectChildren();
      setChildren(res.data.children || []);
      setDepositWallet(res.data.depositWallet || 0);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to load your direct referrals.' });
    } finally {
      setLoadingChildren(false);
    }
  }, []);

  useEffect(() => {
    fetchChildren();
  }, [fetchChildren]);

  const filteredChildren = children.filter(c =>
    c.name.toLowerCase().includes(searchCode.toLowerCase()) ||
    c.mySponsorCode.toLowerCase().includes(searchCode.toLowerCase()) ||
    (c.phone && c.phone.includes(searchCode))
  );

  const handleSelectChild = (child) => {
    setSelected(child);
    setSearchCode('');
    setMessage(null);
  };

  const handleAmountChange = (e) => {
    const inputValue = e.target.value;

    if (inputValue === '') {
      setAmount('');
      setMessage(null);
      return;
    }

    if (/^\d*(\.\d{0,2})?$/.test(inputValue)) {
      setAmount(inputValue);
      setMessage(null);
    }
  };

  const validateTransferInputs = (requirePassword = false) => {
    if (!selected) return 'Please select a recipient.';

    if (!amount) return 'Enter transfer amount.';

    if (!isAmountValid) {
      return 'Enter a valid amount (up to 2 decimal places).';
    }

    if (parsedAmount > depositWallet) {
      return `Insufficient balance. Available: ₹${depositWallet}`;
    }

    if (requirePassword && !password) {
      return 'Enter password.';
    }

    return null;
  };

  const handleProceed = (e) => {
    e.preventDefault();
    setMessage(null);

    const validationError = validateTransferInputs(false);
    if (validationError) return setMessage({ type: 'error', text: validationError });

    setStep(2);
  };

  const handleConfirm = async () => {
    const validationError = validateTransferInputs(true);
    if (validationError) {
      setMessage({ type: 'error', text: validationError });
      setStep(1);
      return;
    }

    setSubmitting(true);
    setMessage(null);
    try {
      const res = await userAPI.fundTransfer({
        recipientSponsorCode: selected.mySponsorCode,
        amount: parsedAmount,
        password,
      });
      setMessage({ type: 'success', text: res.data.message });
      setDepositWallet(res.data.senderNewBalance);
      setSelected(null);
      setAmount('');
      setPassword('');
      setStep(1);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Transfer failed. Please try again.' });
      setStep(1);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingChildren) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="w-8 h-8 border-4 border-[#E4572E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#0F2F3D]">Fund Transfer</h1>
        <p className="text-sm text-gray-500 mt-1">Transfer money from your deposit wallet to your team members.</p>
      </div>

      {/* Balance card */}
      <div className="bg-gradient-to-r from-[#0F2F3D] to-[#1a4a60] rounded-xl p-4 mb-6 text-white flex items-center justify-between">
        <div>
          <p className="text-xs opacity-75">Your Deposit Wallet</p>
          <p className="text-2xl font-bold mt-0.5">₹{depositWallet.toLocaleString('en-IN')}</p>
        </div>
        <span className="text-3xl">💰</span>
      </div>

      {/* Alert */}
      {message && (
        <div className={`mb-5 px-4 py-3 rounded-lg text-sm font-medium ${
          message.type === 'success'
            ? 'bg-green-50 border border-green-200 text-green-700'
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? '✅ ' : '❌ '}{message.text}
        </div>
      )}

      {/* No children */}
      {children.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5 text-center text-sm text-yellow-700">
          You have no team members yet. You can transfer funds only to users in your team.
        </div>
      )}

      {/* STEP 1 — Select recipient & amount */}
      {step === 1 && children.length > 0 && (
        <form onSubmit={handleProceed} className="space-y-5">
          {/* Recipient selection */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4">
            <h2 className="text-base font-semibold text-[#0F2F3D]">👤 Select Team Member</h2>

            {/* Search */}
            <input
              type="text"
              value={searchCode}
              onChange={(e) => setSearchCode(e.target.value)}
              placeholder="Search team by name, sponsor code or phone..."
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E4572E]"
            />

            {/* Selected chip */}
            {selected && (
              <div className="flex items-center justify-between bg-[#0F2F3D] text-white px-4 py-2.5 rounded-lg text-sm">
                <div>
                  <span className="font-semibold">{selected.name}</span>
                  <span className="ml-2 opacity-70 text-xs">{selected.mySponsorCode}</span>
                </div>
                <button type="button" onClick={() => setSelected(null)} className="text-white opacity-60 hover:opacity-100 ml-3">✕</button>
              </div>
            )}

            {/* Dropdown list when searching */}
            {searchCode && (
              <div className="border border-gray-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                {filteredChildren.length === 0 ? (
                  <p className="text-sm text-gray-400 px-4 py-3">No matching referrals found.</p>
                ) : (
                  filteredChildren.map(child => (
                    <button
                      key={child._id}
                      type="button"
                      onClick={() => handleSelectChild(child)}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b last:border-0 text-sm flex justify-between items-center"
                    >
                      <div>
                        <span className="font-medium text-gray-800">{child.name}</span>
                        <span className="ml-2 text-xs text-gray-400">{child.mySponsorCode}</span>
                        {child.phone && <span className="ml-2 text-xs text-gray-400">{child.phone}</span>}
                        <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full ${child.relationType === 'DIRECT' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                          {child.relationType === 'DIRECT' ? 'DIRECT' : 'TEAM'}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400">₹{(child.depositWallet || 0).toLocaleString('en-IN')}</span>
                    </button>
                  ))
                )}
              </div>
            )}

            {/* All children list (when nothing selected and not searching) */}
            {!selected && !searchCode && (
              <div className="border border-gray-200 rounded-lg overflow-hidden max-h-52 overflow-y-auto">
                {children.map(child => (
                  <button
                    key={child._id}
                    type="button"
                    onClick={() => handleSelectChild(child)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b last:border-0 text-sm flex justify-between items-center"
                  >
                    <div>
                      <span className="font-medium text-gray-800">{child.name}</span>
                      <span className="ml-2 text-xs text-gray-400">{child.mySponsorCode}</span>
                      <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full ${child.relationType === 'DIRECT' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                        {child.relationType === 'DIRECT' ? 'DIRECT' : 'TEAM'}
                      </span>
                    </div>
                    <span className="text-xs text-green-600 font-medium">₹{(child.depositWallet || 0).toLocaleString('en-IN')}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Amount */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-3">
            <h2 className="text-base font-semibold text-[#0F2F3D]">💸 Amount</h2>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">₹</span>
              <input
                type="number"
                min="1"
                max={depositWallet}
                step="0.01"
                value={amount}
                onChange={handleAmountChange}
                placeholder="0"
                className="w-full border border-gray-300 rounded-lg pl-8 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E4572E]"
              />
            </div>
            {amount && !isAmountValid && (
              <p className="text-xs text-red-500">Enter valid amount (minimum ₹280)</p>
            )}
            {isAmountValid && parsedAmount > depositWallet && (
              <p className="text-xs text-red-500">Amount cannot be more than available deposit wallet.</p>
            )}
            {/* Quick amounts */}
            <div className="flex gap-2 flex-wrap">
              {[280, 500, 1000, 2000, 5000].map(q => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setAmount(String(q))}
                  disabled={q > depositWallet}
                  className="px-3 py-1 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  ₹{q}
                </button>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={!selected || !isAmountValid || parsedAmount > depositWallet}
            className="w-full bg-[#E4572E] hover:bg-[#c94a26] text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Review Transfer →
          </button>
        </form>
      )}

      {/* STEP 2 — Confirm */}
      {step === 2 && selected && (
        <div className="space-y-5">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4">
            <h2 className="text-base font-semibold text-[#0F2F3D]">🔍 Confirm Transfer</h2>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">From</span>
                <span className="font-medium text-gray-800">Your Deposit Wallet</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">To</span>
                <div className="text-right">
                  <p className="font-semibold text-gray-800">{selected.name}</p>
                  <p className="text-xs text-gray-400">{selected.mySponsorCode}</p>
                </div>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">Amount</span>
                <span className="text-lg font-bold text-[#E4572E]">₹{Number(amount).toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-500">Balance After Transfer</span>
                <span className="font-semibold text-gray-700">₹{(depositWallet - Number(amount)).toLocaleString('en-IN')}</span>
              </div>
            </div>

            <div>
              <label className="block mb-2 text-sm font-semibold text-[#0F2F3D]">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your login password"
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E4572E]"
                required
              />
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => { setStep(1); setMessage(null); }}
              disabled={submitting}
              className="flex-1 border border-gray-300 text-gray-600 font-semibold py-3 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-60"
            >
              ← Back
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={submitting}
              className="flex-grow bg-[#E4572E] hover:bg-[#c94a26] text-white font-semibold py-3 px-6 rounded-xl transition-colors disabled:opacity-60"
            >
              {submitting ? 'Transferring...' : 'Confirm Transfer'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserFundTransfer;
